import { createParser } from 'nuqs'
import type { FilterOperator, FilterState, SortState } from 'types/data-grid'

/**
 * Compact URL-friendly encoders for DataGrid state. We roll our own instead
 * of nuqs's parseAsJson so the URL bar stays readable — the separators are
 * all RFC 3986 unreserved characters (alphanumeric, `.`, `_`, `~`, `-`) so
 * URLSearchParams doesn't percent-encode them.
 *
 * Formats:
 *   Sort:    `<column>.<direction>`              e.g. `firstName.asc`
 *   Filter:  `<field>.<operator>[.<value>]`      joined by `~`
 *                                                 e.g. `firstName.like.bob~email.present`
 *   Page:    plain integer (via nuqs `parseAsInteger`)
 */

const FILTER_SEPARATOR = '~'
const OPERATORS: ReadonlySet<FilterOperator> = new Set(['like', 'present', 'empty'])

function isOperator(raw: string): raw is FilterOperator {
  return OPERATORS.has(raw as FilterOperator)
}

function parseSortString(raw: string): SortState | null {
  const dot = raw.indexOf('.')
  if (dot <= 0) {
    return null
  }
  const column = raw.slice(0, dot)
  const direction = raw.slice(dot + 1)
  if (direction !== 'asc' && direction !== 'desc') {
    return null
  }
  return { column, direction }
}

function serializeSortString(state: SortState): string {
  return `${state.column}.${state.direction}`
}

function parseFilterEntry(part: string): [string, FilterState] | null {
  const firstDot = part.indexOf('.')
  if (firstDot <= 0) {
    return null
  }
  const field = part.slice(0, firstDot)
  const rest = part.slice(firstDot + 1)

  // "present" / "empty" are operator-only.
  if (isOperator(rest) && rest !== 'like') {
    return [field, { operator: rest, value: '' }]
  }

  // `like` may be bare (empty value) or followed by `.<value>`. We split on
  // the FIRST dot only so values can contain dots (e.g. "bob@example.com").
  if (rest === 'like') {
    return [field, { operator: 'like', value: '' }]
  }
  if (rest.startsWith('like.')) {
    return [field, { operator: 'like', value: rest.slice('like.'.length) }]
  }
  return null
}

function parseFiltersString(raw: string): Record<string, FilterState> | null {
  const out: Record<string, FilterState> = {}
  for (const part of raw.split(FILTER_SEPARATOR)) {
    const entry = parseFilterEntry(part)
    if (entry) {
      out[entry[0]] = entry[1]
    }
  }
  return Object.keys(out).length > 0 ? out : null
}

function serializeFiltersString(filters: Record<string, FilterState>): string {
  const parts: string[] = []
  for (const [field, filter] of Object.entries(filters)) {
    if (filter.operator === 'like') {
      parts.push(filter.value ? `${field}.like.${filter.value}` : `${field}.like`)
    } else {
      parts.push(`${field}.${filter.operator}`)
    }
  }
  return parts.join(FILTER_SEPARATOR)
}

export const parseSort = createParser<SortState>({
  parse: parseSortString,
  serialize: serializeSortString,
  eq: (a, b) => a?.column === b?.column && a?.direction === b?.direction,
})

export const parseFilters = createParser<Record<string, FilterState>>({
  parse: parseFiltersString,
  serialize: serializeFiltersString,
  // Shallow equality — sufficient for downstream memoization since callers
  // always replace the whole record, never mutate in place.
  eq: (a, b) => {
    if (a === b) {
      return true
    }
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) {
      return false
    }
    for (const key of keysA) {
      const fa = a[key]
      const fb = b[key]
      if (fa.operator !== fb?.operator || fa.value !== fb.value) {
        return false
      }
    }
    return true
  },
})
